import { useMemo, useState } from 'react'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Box,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

import {
  COLOR_CX_CHEAPER,
  COLOR_DRIVER_APP_CHEAPER,
  DRIVER_APP_FULFILMENT_CHART_HEIGHT,
  PRICE_DIFFERENCE_INFO,
  PRICE_DIFFERENCE_VIEW_INFO,
  PRICE_DIFFERENCE_VIEWS,
  PriceDifferenceView,
} from 'components/reports/Metrics/constants/DriverAppFulfilment'
import { DriverAppFulfilmentPriceChartDto } from 'generated/graphql'
import { toLines } from 'helpers/bucketBarChart.helpers'
import { colorOf, formatCount, formatPercentage } from 'helpers/priceDifferenceChart.helpers'

import { BucketBarChart } from '../components/BucketBarChart'

interface Props {
  data?: DriverAppFulfilmentPriceChartDto | null
  loading?: boolean
}

export const PriceDifferenceCard = ({ data, loading }: Props) => {
  const [view, setView] = useState<PriceDifferenceView>('percentage')

  const isPercentage = view === 'percentage'
  const isValue = view === 'value'

  const chart = useMemo(() => {
    const buckets = (isValue ? data?.valueBuckets : data?.percentageBuckets) ?? []

    return {
      labels: buckets.map((bucket) => toLines(bucket.label)),
      colors: buckets.map((bucket) => colorOf(bucket.label)),
      values: buckets.map((bucket) => (isPercentage ? Number(bucket.percentage) : bucket.jobCount)),
    }
  }, [data, isPercentage, isValue])

  const comparable = data?.comparableJobs ?? 0
  const cxFulfilled = data?.cxFulfilledJobs ?? 0

  return (
    <Paper
      variant='outlined'
      sx={{
        p: 2.5,
        height: '100%',
        borderRadius: 2,
        borderTop: `3px solid ${COLOR_DRIVER_APP_CHEAPER}`,
      }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', md: 'flex-start' }}
        mb={2}>
        <Box>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography variant='subtitle1' fontWeight={700}>
              Price difference: CX winning bid vs. lowest Driver App bid
            </Typography>
            <Tooltip title={PRICE_DIFFERENCE_INFO}>
              <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
            </Tooltip>
          </Stack>
          <Typography variant='body2' color='primary.main'>
            For jobs not fulfilled through Driver App. Negative values mean CX was cheaper.
          </Typography>
        </Box>

        <Stack direction='row' spacing={1} alignItems='center'>
          <TextField
            select
            size='small'
            value={view}
            onChange={(event) => setView(event.target.value as PriceDifferenceView)}
            sx={{ minWidth: 180 }}>
            {PRICE_DIFFERENCE_VIEWS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip
            title={PRICE_DIFFERENCE_VIEW_INFO}
            slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 420 } } }}>
            <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
          </Tooltip>
        </Stack>
      </Stack>

      {loading ? (
        <Skeleton
          variant='rectangular'
          height={DRIVER_APP_FULFILMENT_CHART_HEIGHT}
          sx={{ borderRadius: 1 }}
        />
      ) : (
        <BucketBarChart
          labels={chart.labels}
          series={[
            {
              label: isPercentage ? '% of failed jobs' : 'Number of jobs',
              color: chart.colors,
              values: chart.values,
            },
          ]}
          yAxisLabel={isPercentage ? '% of failed jobs' : 'Number of jobs'}
          valueFormatter={isPercentage ? formatPercentage : formatCount}
          height={DRIVER_APP_FULFILMENT_CHART_HEIGHT}
        />
      )}

      <Stack
        direction='row'
        spacing={2}
        alignItems='center'
        justifyContent='space-between'
        sx={{ mt: 1.5, px: 1 }}>
        <Stack direction='row' spacing={0.5} alignItems='center' sx={{ color: COLOR_CX_CHEAPER }}>
          <ArrowBackIcon fontSize='small' />
          <Typography variant='body2' fontWeight={600}>
            CX cheaper
          </Typography>
        </Stack>

        <Typography variant='body2' fontWeight={600} textAlign='center'>
          {isValue
            ? 'Price difference in £ (CX winning bid − lowest Driver App bid)'
            : 'Price difference (CX winning bid − lowest Driver App bid)'}
        </Typography>

        <Stack
          direction='row'
          spacing={0.5}
          alignItems='center'
          sx={{ color: COLOR_DRIVER_APP_CHEAPER }}>
          <Typography variant='body2' fontWeight={600}>
            Driver App cheaper
          </Typography>
          <ArrowForwardIcon fontSize='small' />
        </Stack>
      </Stack>

      <Stack direction='row' spacing={1} alignItems='flex-start' mt={1.5}>
        <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
        <Typography variant='caption' color='text.secondary'>
          Based on {comparable.toLocaleString()} of {cxFulfilled.toLocaleString()} jobs fulfilled
          through CX with both a live Driver App bid and an accepted CX price, converted to GBP.
          Each band includes its lower bound and excludes its upper bound.
        </Typography>
      </Stack>
    </Paper>
  )
}
